import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import Actionbuttons from 'components/common/actions-buttons';
import { sanitizeDescriptionRemovePayee } from 'components/ledger-entry-editor/utils/sanitize';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalize, IntlCurrencyFormat } from 'utils/helpers';

import { TableLedgerItemColumn } from '../../../services';
import useAccountingContext from '../../../services/useAccountingContext';
import { processCode } from '../services';
import AccountedFor from './accounted-for';
import FieldRow from './field-row';
import InvolvedPartiesList from './involved-parties-list';

const useStyles = makeStyles((theme: any) => ({
  card: {
    marginBottom: theme.spacing(1.5),
  },
  content: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  toggle: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(-0.5),
    flexShrink: 0,
  },
  summary: {
    flex: 1,
    minWidth: 0,
  },
  faked: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: theme.palette.primary.light,
  },
  description: {
    fontSize: '0.875rem',
    color: theme.palette.primary.darker,
    overflowWrap: 'anywhere',
  },
  amount: {
    flexShrink: 0,
    textAlign: 'right',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.primary.darker,
    fontVariantNumeric: 'tabular-nums',
  },
  actionsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1),
    '& button': {
      padding: theme.spacing(1.5),
    },
  },
  detail: {
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.button.default}`,
  },
  section: {
    marginTop: theme.spacing(1.5),
  },
  sectionTitle: {
    marginBottom: theme.spacing(0.75),
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
}));

type LedgerCardProps = {
  row: TableLedgerItemColumn;
  isOpen: boolean;
  onToggle: () => void;
};

const LedgerCard: FC<LedgerCardProps> = ({ row, isOpen, onToggle }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    handleAddParty,
    handleEditAmount,
    handleEditLedgerAssociation,
    handleDeleteLedgetEntry,
    handleDeleteLegerAssociation,
    handleCreateLink,
  } = useAccountingContext()! || {};

  const isFaked = (row.amount_faked ?? 0) > 0;
  const kind = row.kind || '';
  const enableDelete =
    kind.includes('manual') || kind.includes('disbursement') || kind.includes('receipt');
  const detailId = `ledger-card-detail-${row.id}`;
  const postedText =
    row.posted === null || row.posted === undefined ? '' : t(`accounting:${row.posted}`);
  const description = sanitizeDescriptionRemovePayee(row.description, row.involved_parties);
  const parties = row.involved_parties || [];

  return (
    <Card className={classes.card}>
      <CardContent className={classes.content}>
        <Box className={classes.headerRow}>
          <IconButton
            className={classes.toggle}
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-controls={detailId}
            aria-label={row.description}
            size="large"
          >
            <FontAwesomeIcon
              icon={isOpen ? 'arrow-alt-circle-up' : 'arrow-alt-circle-down'}
              size="sm"
            />
          </IconButton>

          <Box className={classes.summary}>
            {isFaked && (
              <Typography component="div" className={classes.faked}>
                {t('accounting:faked')}
              </Typography>
            )}
            <Typography component="div" className={classes.description}>
              {description}
            </Typography>
          </Box>

          <Typography component="div" className={classes.amount}>
            {isFaked
              ? IntlCurrencyFormat(row.amount_faked ?? 0)
              : IntlCurrencyFormat(row.amount)}
          </Typography>
        </Box>

        <Box className={classes.actionsRow}>
          <Actionbuttons
            handleAdd={() => handleAddParty(row)}
            handleEdit={() => handleEditAmount(row)}
            handleDelete={enableDelete ? () => handleDeleteLedgetEntry(row) : undefined}
            handleChain={() => handleCreateLink(row)}
          />
        </Box>

        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box id={detailId} className={classes.detail}>
            <FieldRow label={t('accounting:kind')} value={capitalize(kind)} />
            <FieldRow label={t('accounting:code')} value={processCode(row.code)} />
            <FieldRow label={t('accounting:letter')} value={row.letter ?? ''} />
            <FieldRow label={t('orders:numberHeader')} value={row.number ?? ''} />
            <FieldRow label={t('accounting:posted')} value={postedText} />
            <FieldRow
              label={t('accounting:entry-date')}
              value={(row.entryDate && row.entryDate.split('T')[0]) || ''}
            />

            <Box className={classes.section}>
              <Typography component="div" className={classes.sectionTitle}>
                {t('accounting:accounted-for')}
              </Typography>
              <AccountedFor accountedInfo={row.accounted_for || []} isFaked={isFaked} t={t} />
            </Box>

            <Box className={classes.section}>
              <Typography component="div" className={classes.sectionTitle}>
                {`${t('accounting:parties')} (${parties.length})`}
              </Typography>
              <InvolvedPartiesList
                ledgerRow={row}
                handleEditLedgerAssociation={handleEditLedgerAssociation}
                handleDeleteLedgerAssociation={handleDeleteLegerAssociation}
              />
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default LedgerCard;
